import util from '../../../../share/util/utils';
import vehicle from '../../../../share/util/vehicle';
import ApiService from '../../../../share/util/api/service';
import individualDiagnose from "../../../../share/util/individualDiagnose";
import dmDidDataConversion from "../../../../share/util/didDataConversion";

import dmUtil, { WORKER, WORKER_TASK, WORKER_EVENT, DATA_STORAGE } from "../../../pages/dataMonitor/dataMonitorUtil";
import { STORE_NAME, DATA_POINT_STORE } from '../../../pages/dataMonitor/indexedDbManager';
import alert from '../../../../share/alert';

const DIAG_SYSTEM = 'diagSytem.';
const CSV = {
  LINE_BREAK: '\r\n',
  ECU_DECORATION: '################'
};
// Workerの待機間隔
const WORKER_SLEEP_INTERVAL = 500;
// Workerに一度に渡すデータ行数
const WORKER_ROW_LIMIT = 1000;
// CSVのデータが始まる列番号。0列目は時間、1列目はマーカー。
const DATA_COLUMN_NUM = 2;

/**
 * CSV出力を行う
 * @param {object} csvFileWriter csvFileWriterクラスのインスタンス
 * @param {object} currentComponent 呼び出し元のコンポーネントのインスタンス
 * @param {object} dataMonitorComponent データモニタのコンポーネントのインスタンス
 * @param {object} projectDetails プロジェクト情報
 * @param {string} measurementId 計測ID
 * @param {DATA_STORAGE} storage データ保存先
 * @param {function} errorDialogCallback エラーダイアログのボタン押下時のコールバック
 * @param {function} updateExportProgress 進捗更新のコールバック
 * @param {boolean} isDebug デバッグ用か否か。デバッグ用の場合は未変換のデータを出力する
 */
export async function exportCsv({ csvFileWriter, currentComponent, dataMonitorComponent, projectDetails, measurementId, storage, errorDialogCallback, updateExportProgress, isDebug = false }) {
  // Composable内で保持する値
  let workerInstance = {};
  let workerError = false;
  let hasStopOrder = false;
  let csvData = [];
  let indexedDbManager = null;

  let _dataMonitorComponent = dataMonitorComponent;
  if (dataMonitorComponent.value) {
    _dataMonitorComponent = dataMonitorComponent.value;
  }

  try {
    // 子コンポーネントから必要な情報を収集
    const { conversionList, selectedFuncs, baseTime, escapedDataPoints } = _dataMonitorComponent;

    if (!baseTime) {
      // baserTimeがないデータは出力するデータがないため処理終了
      return;
    }

    const markers = _dataMonitorComponent.getMarkers();
    const markerTimes = markers.map(marker => Object.values(marker)[0].markerTime).sort();
    const measureData = !isDebug ? _dataMonitorComponent.getMeasureData() : _dataMonitorComponent.getDebugOriginData();
    indexedDbManager = _dataMonitorComponent.indexedDbManager;

    if (storage === DATA_STORAGE.S3 && (!indexedDbManager || !indexedDbManager.isAvailable())) {
      // 容量不足ダイアログは、OK押下後も画面遷移しない
      alert.showWarning(currentComponent, null, currentComponent.$t('individual.label_data_monitor_csv_fail_insufficient_storage'), () => { });
      return;
    }

    // CSV用の文字列作成
    const descriptionStr = createCsvDescription();

    const itemIds = selectedFuncs.map(selected => selected.data_monitor_id);
    const itemsBySystem = dmUtil.getItemsBySystem(itemIds);

    // CSVのヘッダー上部の計測時の情報の書き込み
    await csvFileWriter.write(descriptionStr);

    // システムごとにCSVのヘッダーとデータ部を作成
    for (const systemName of Object.keys(itemsBySystem)) {
      const region = individualDiagnose.getRegion(currentComponent, null, projectDetails, measurementId, systemName);
      const headerStr = createCsvHeader(conversionList, itemsBySystem[systemName], systemName, region);

      if (Object.keys(itemsBySystem)[0] !== systemName) {
        // 2つ目以降のシステムは、前のシステムと区切るために改行を入れる
        await csvFileWriter.write(CSV.LINE_BREAK);
      }
      // CSVのヘッダーの書き込み
      await csvFileWriter.write(headerStr);

      // 対象システムの選択項目と計測データのみにフィルタ
      const filteredSelected = selectedFuncs.filter(selected => dmUtil.getSystem(selected.data_monitor_id) === systemName);
      await createCsvBodyStr(systemName, conversionList, measureData, escapedDataPoints, filteredSelected, markerTimes, region, baseTime);
    }
    alert.showInfo(currentComponent, null, currentComponent.$t('individual.data_monitor_csv_export_complete_message'));
  } catch (error) {
    console.error(error);
    alert.showError(currentComponent, null, currentComponent.$t('message.common_systemError'), errorDialogCallback);
  } finally {
    if (workerInstance instanceof Worker) {
      // Workerの停止と終了
      workerInstance.postMessage({ type: WORKER_TASK.STOP, worker: WORKER.MEASUREMENT_CSV });
      workerInstance.terminate();
      workerInstance = {};
    }
  }

  /**
   * CSVデータの文字列作成
   * @param {string} systemName システム名
   * @param {array} conversionList 変換情報のリスト
   * @param {object} measureData 計測データ
   * @param {object} escapedDataPoints RestfulWorkerで除外されたデータ
   * @param {array} filteredSelected 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {string} region リージョン
   * @param {number} baseTime 基準時刻
   * @returns {Promise<string>} CSVデータの文字列
   */
  async function createCsvBodyStr(systemName, conversionList, measureData, escapedDataPoints, filteredSelected, markerTimes, region, baseTime) {
    if (storage === DATA_STORAGE.S3) {
      let data = {};
      const allCounts = await indexedDbManager.asyncCount(STORE_NAME.DATA_POINT);
      let dbId = 0;
      let firstTime = 0;
      do {
        // CSV出力データをindexedDBから取得
        const result = await readFromIndexedDb(indexedDbManager, dbId);
        data = result.data;
        dbId++;

        if (Object.keys(data).length) {
          const filteredMeasured = {};
          // RestfulWorkerでエスケープしたデータのマージ
          filteredSelected.forEach(item => {
            // IndexedDBから取り出したデータの間で、RestfulWorkerにて退避したデータがあれば追加
            const fillingTarget = (escapedDataPoints[systemName]?.[item.data_monitor_id] || [])
              .filter(d => firstTime < d.time && d.time <= result.lastTime);

            filteredMeasured[item.data_monitor_id] = (data[item.data_monitor_id] || [])
              .concat(fillingTarget)
              .sort((a, b) => (a[0] || a.time) - (b[0] || b.time));
            (data[item.data_monitor_id] || []).splice(0);
          });

          await generateCsvFromData(conversionList, filteredSelected, markerTimes, filteredMeasured, region, baseTime, systemName);

          // 今回の退避データ対象の最終時刻を、次の退避データ対象の初回時刻に設定
          firstTime = result.lastTime;
          // 進捗更新(表示データと間引きデータがあるため、全体の数を2で割った数を全体数、現在の数は処理したdbId)
          updateExportProgress(allCounts / 2, dbId);
        }
      } while (data && Object.keys(data).length);

    } else {
      const filteredMeasured = {};
      filteredSelected.forEach(item => {
        // RestfulWorkerでエスケープしたデータのマージ
        filteredMeasured[item.data_monitor_id] = [
          ...(measureData[item.data_monitor_id] || []),
          ...(escapedDataPoints[systemName]?.[item.data_monitor_id] || [])
        ].sort((a, b) => a.time - b.time);
      });
      await generateCsvFromData(conversionList, filteredSelected, markerTimes, filteredMeasured, region, baseTime, systemName, true);
    }
  }

  /**
   * CSVデータの作成とCSVファイルへの書き込み
   * @param {array} conversionList 変換情報のリスト
   * @param {array} filteredSelected 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measuredData 計測データ
   * @param {string} region リージョン
   * @param {number} baseTime 基準時刻
   * @param {string} systemName システム名
   * @param {boolean} isIntdash データ取得先がIntdashか否か
   */
  async function generateCsvFromData(conversionList, filteredSelected, markerTimes, measuredData, region, baseTime, systemName, isIntdash = false) {
    const measurementCount = Object.keys(measuredData).map(did => (measuredData[did] ? measuredData[did].length : 0)).reduce((a, b) => Math.max(a, b), -Infinity);
    let dataStr = '';
    const allCounts = Math.ceil(measurementCount / WORKER_ROW_LIMIT); // Workerに渡す回数の全体数
    // 大量データをWorkerのメッセージで送受信すると遅くなるため、一定行ごとに処理を行う
    for (let i = 0; i * WORKER_ROW_LIMIT < measurementCount; i++) {
      const obj = {};
      for (const did in measuredData) {
        obj[did] = measuredData[did].splice(0, WORKER_ROW_LIMIT);
      }
      const partialDataStr = await createCsvData(conversionList, filteredSelected, markerTimes, obj, region, baseTime, isDebug);
      dataStr = dataStr + partialDataStr;

      if (isIntdash) {
        // Intdashからのデータの場合、Workerの実行回数を進捗とする
        updateExportProgress(allCounts, i + 1);
      }
    }

    // CSVファイルへの書き込み
    await csvFileWriter.write(dataStr);
  }

  /**
 * CSVのヘッダー上部の計測時の情報の文字列を作成
 * @returns {string} CSVのヘッダー上部の内容の文字列
 */
  function createCsvDescription() {
    const targetMeasurement = projectDetails.measures[0][ApiService.DMAT].find(measure => measure.measurement_id === measurementId);

    const dataName = targetMeasurement.name || '';
    const memo = targetMeasurement.note || '';
    const vin = projectDetails.information.vin || '';
    const registrationNumber = (projectDetails.area_name ? projectDetails.area_name + ' ' : '')
      + (projectDetails.category ? projectDetails.category + ' ' : '')
      + (projectDetails.hiragana ? projectDetails.hiragana + ' ' : '')
      + (projectDetails.individual_number ? projectDetails.individual_number + ' ' : '');
    const vehicleName = projectDetails.information.vehicle_name || '';
    // 再生画面ではvehicleInfoのtypeが取得できないため、VINのフォーマットから判定
    const isVin = vehicle.isVinType(vin);
    const vehicleType = (isVin ? projectDetails.information.model_year : projectDetails.information.classification) || '';

    const dataNameDescription = `"${currentComponent.$t('individual.header_data_monitor_data_name')}",${util.escapeForCsv(dataName)}`;
    const memoDescription = `"${currentComponent.$t('individual.label_memo')}",${util.escapeForCsv(memo)}`;
    // vinの種類によって表示する行を変える
    const vinDescription = `"${currentComponent.$t('individual.header_data_monitor_vin')}",${isVin ? util.escapeForCsv(vin) : util.escapeForCsv('')}`;
    const frameNumberDescription = `"${currentComponent.$t('individual.header_data_monitor_frame_number')}",${isVin ? util.escapeForCsv('') : util.escapeForCsv(vin)}`;
    const registrationNumDescription = `"${currentComponent.$t('individual.header_data_monitor_registration_number')}",${util.escapeForCsv(registrationNumber)}`;
    const vehicleNameDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_name')}",${util.escapeForCsv(vehicleName)}`;
    const vehicleTypeDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_type')}",${util.escapeForCsv(vehicleType)}`;

    return [
      dataNameDescription, memoDescription, vinDescription, frameNumberDescription, registrationNumDescription, vehicleNameDescription, vehicleTypeDescription
    ].join(CSV.LINE_BREAK) + CSV.LINE_BREAK;
  }

  /**
   * CSVのヘッダーの文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} itemIds 計測時の選択項目。CSVの列は選択項目の順番で出力する。(e.g. ['101A_1_all', '1012_3_5'])
   * @param {string} systemName システム名
   * @param {string} region リージョン
   * @returns {string} CSVのヘッダーの内容の文字列
   */
  function createCsvHeader(conversionList, itemIds, systemName, region) {
    const findFFDi18nKey = individualDiagnose.getFFDi18nKeyWithRegion(
      currentComponent, region, systemName
    );

    let firstHeader = itemIds.map(item => {
      let translateKey = '';
      if (conversionList[dmUtil.setSystem(systemName, item.id)]?.active_test_id) {
        // HEV系AT IO/Routine Control項目の場合、独自にキーを取得
        translateKey = `at${systemName}.${conversionList[dmUtil.setSystem(systemName, item.id)].active_test_id}.menu_title`;
      } else {
        translateKey = `${findFFDi18nKey}.${item.id}`;
      }
      return util.escapeForCsv(currentComponent.$t(translateKey));
    });
    firstHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark')}"`);
    firstHeader = firstHeader.join(',');

    let secondHeader = itemIds.map(item => util.escapeForCsv(
      conversionList[dmUtil.setSystem(systemName, item.id)]?.unit_id ? currentComponent.$t(`didUnit.` + conversionList[dmUtil.setSystem(systemName, item.id)]?.unit_id) : ''
    ));
    secondHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time_unit')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark_unit')}"`);
    secondHeader = secondHeader.join(',');

    const ecuNameDescription = `"${CSV.ECU_DECORATION} ${currentComponent.$t(DIAG_SYSTEM + systemName)} ${CSV.ECU_DECORATION}"`;
    return ecuNameDescription + CSV.LINE_BREAK + firstHeader + CSV.LINE_BREAK + secondHeader + CSV.LINE_BREAK;
  }

  /**
   * CSVのデータ部の文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} selectedFuncs 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {string} region リージョン
   * @param {number} baseTime 基準時刻
   * @param {boolean} isDebug デバッグ用か否か。デバッグ用の場合は未変換のデータを出力する
   * @returns {Promise<string>} CSVのデータ部の内容の文字列
   */
  async function createCsvData(conversionList, selectedFuncs, markerTimes, measureData, region, baseTime, isDebug = false) {
    // WorkerでCSVのベースデータ作成
    await startWorker(conversionList, markerTimes, measureData, selectedFuncs, baseTime, isDebug);

    const didList = selectedFuncs.map(item => item.data_monitor_id);

    // CSV全体の行ごとのループ
    const translatedCsvData = csvData.map(row => {
      // 1行の列ごと（計測項目ごと）のループ
      const translatedRow = row.map((cell, index) => {
        if (index < DATA_COLUMN_NUM) {
          // データ部でない列はそのまま使用
          return cell;
        }
        const did = didList[index - DATA_COLUMN_NUM];
        // データ部の列は変換と翻訳処理を行う
        return util.escapeForCsv(String(!isDebug ? translate(cell, did, conversionList[did], region) : cell));
      });
      return translatedRow.join(',') + CSV.LINE_BREAK;
    });

    // Workerから返された元のCSV情報はクリア
    csvData.splice(0);

    return translatedCsvData.join('');
  }

  /**
   * 値の翻訳と変換を実施する
   * @param {string} originValue 翻訳・変換対象の値
   * @param {string} did 計測項目
   * @param {object} conversionInfo 変換情報 
   * @param {string|null} region リージョン文字列
   * @returns {string} 翻訳・変換後の値
   */
  function translate(originValue, did, conversionInfo, region) {
    if (originValue && dmUtil.VALUE_TRANSLATE_TYPE.includes(conversionInfo.data_type)) {
      const [systemName, itemId] = dmUtil.splitSystemAndItemId(did);
      const translated = dmUtil.getTranslateValue(currentComponent, itemId, originValue, conversionInfo,
        region, systemName, conversionInfo.active_test_id || null);
      if (conversionInfo.data_type === 'decExStr') {
        return translated || dmDidDataConversion.convertValueDataForHex(originValue, conversionInfo);
      } else {
        return translated;
      }
    } else {
      return originValue;
    }
  }

  /**
   * Web workerの開始処理
   * @param {object} conversionList 変換情報のリスト
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {array} selectedFuncs 計測時の選択項目
   * @param {number} baseTime 基準時刻
   * @param {boolean} isDebug デバッグ用か否か。デバッグ用の場合は未変換のデータを出力する
   */
  async function startWorker(conversionList, markerTimes, measureData, selectedFuncs, baseTime, isDebug = false) {
    // Workerを作成
    if (!(workerInstance instanceof Worker)) {
      // webpackChunkNameを設定した"data-monitor-worker"が、ビルド時のファイル名に使用される
      workerInstance = new Worker(
          /* webpackChunkName: "data-monitor-worker" */ new URL('../../../pages/dataMonitor/DataMonitorWorker.js', import.meta.url)
      );
    }

    const startMessage = {
      worker: WORKER.MEASUREMENT_CSV,
      type: WORKER_TASK.START,
      measureData: JSON.parse(JSON.stringify(measureData)),
      markerTimes: JSON.parse(JSON.stringify(markerTimes)),
      conversionList: JSON.parse(JSON.stringify(conversionList)),
      selectedFuncs: JSON.parse(JSON.stringify(selectedFuncs)),
      baseTime: baseTime,
      isDebug: isDebug
    };

    // Workerに処理開始メッセージを送信
    hasStopOrder = false;
    workerInstance.postMessage(startMessage);

    // Workerエラーフラグのリセット
    workerError = false;

    // Workerからメッセージを受信したときの処理
    workerInstance.onmessage = (event) => {
      csvWorkerListener(event);
    };


    // Workerの処理が完了するまで待機する
    while (!hasStopOrder) {
      // Workerでエラーがあった場合は待機を終了
      if (workerError) {
        break;
      }

      // 一定時間スリープ
      await util.sleep(WORKER_SLEEP_INTERVAL);
    }
  }

  /**
   * csvWorkerからのメッセージ受信処理
   * @param {object} event 
   */
  function csvWorkerListener(event) {
    const data = event.data;

    // エラー受信の場合
    if (data.type === WORKER_EVENT.RUNTIME_ERROR) {
      // Workerでエラーあり
      workerError = true;
      // エラーダイアログ表示
      alert.showError(currentComponent, null, currentComponent.$t('message.common_systemError'), errorDialogCallback);
    }
    // 正常受信(CSVデータ)の場合
    else if (data.type === WORKER_EVENT.CSV_DATA) {
      csvData = csvData.concat(data.csvData);
    }
    // 正常受信(処理完了)の場合
    else if (data.type === WORKER_EVENT.SUCCESS_END) {
      hasStopOrder = true;
    }
  }
}

/**
 * IndexedDBからデータ取得
 * @param {IndexedDbManager} indexedDbManager IndexedDbManagerクラスのインスタンス
 * @param {number} dbId 取得するレコードの連番
 * @returns {Promise<object>} 取得結果。なければ空オブジェクト
 */
async function readFromIndexedDb(indexedDbManager, dbId) {
  let lastTime = 0;
  // CSV出力データをindexedDBから取得
  const results = await Promise.all([
    indexedDbManager.asyncRead(STORE_NAME.DATA_POINT, `${dbId}_${DATA_POINT_STORE.ID_TYPE.SHOW_DATA}`),
    indexedDbManager.asyncRead(STORE_NAME.DATA_POINT, `${dbId}_${DATA_POINT_STORE.ID_TYPE.DECIMATED_DATA}`)
  ]);
  const data = results[0]?.d ? results[0].d : {};
  const decimatedData = results[1]?.d ? results[1].d : {};

  // 表示データと間引きデータをマージ。間引きデータのみは対象外。
  if (Object.keys(data).length) {
    for (const did of Object.keys(data)) {
      const didLastTime = Math.max(
        (data[did] || []).length ? data[did][data[did].length - 1][0] : 0,
        (decimatedData[did] || []).length ? decimatedData[did][decimatedData[did].length - 1][0] : 0
      );
      // さらに遅い時間のデータがあれば更新
      lastTime = lastTime < didLastTime ? didLastTime : lastTime;

      data[did] = data[did].concat((decimatedData[did] || []));
    }
  }
  return { data, lastTime };
}
